import { ObservableMap } from '@stencil/store';
import isEmpty from 'lodash.isempty';

import { Account } from '../../models/account';
import { isNonCloud } from '../../utils/EnvironmentUtil';
import { caseInsensitiveCompare } from '../../utils/helper';
import Logger from '../../utils/logger/logger';
import Pendo from '../../utils/pendo/pendo';
import { getAccessToken } from '../../utils/sessionHelper';
import {
    accountStorageKey,
    currentTenantStorageKey,
} from '../../utils/storageKeys';
import {
    isFalselyOrWhiteSpace,
    setActiveLogoUrl,
    setMenuItems,
} from '../common';
import { State } from '../types/state';

export function setAccountSideEffect(store: ObservableMap<State>) {
    store.onChange('account', (account: Account) => {
    // always set the localstorage, regardless of whether or not the account exists
        localStorage.setItem(accountStorageKey, JSON.stringify(account));

        if (!account) {
            return;
        }

        // set default items
        setMenuItems(store);

        Logger.debug('store', 'account has changed', { object: account });

        setActiveLogoUrl(store, account, store.get('theme'));

        store.set('tenantList', account.tenants);

        // empty list scenario.
        if (isEmpty(account.tenants)) {
            Logger.debug('store', 'no tenants returned');
            store.set('currentTenantInfo', undefined);
            store.set('currentTenant', '');

            // let the consumer know that no tenants are available
            const tenantNotAvailableEmitter = store.get('tenantsNotAvailableEmitter');
            tenantNotAvailableEmitter?.emit();
        } else {
            setStateWithTenantNameOrId(account, store);
        }

        const pendo: Pendo = window.PortalShell.Pendo;
        const accessToken = getAccessToken(store);
        if (account && accessToken) {
            Logger.debug('pendo', 'updating all pendo options');
            pendo.updateOptionsFromStore(store);
        }

        Logger.debug(
            'pendo',
            `Pendo init from account sideeffect: ${pendo.pendoInstance !=
        null}, isNonCloud: ${isNonCloud()}, isPendoInitialized: ${
                pendo.isInitialized
            }, accountInfo exists: ${!!account}, token exists: ${!!accessToken}`,
        );
        if (account && accessToken && !isNonCloud()) {
            pendo.initializeFromStore(store);
        }
    });
}

function setStateWithTenantNameOrId(
    account: Account,
    store: ObservableMap<State>,
) {
    const currentTenantNameOrId = store.get('currentTenant');
    const currentTenant = isFalselyOrWhiteSpace(currentTenantNameOrId)
        ? null
        : getTenantFromAccount(account, currentTenantNameOrId);
    const cachedTenant = getCachedTenant(account);
    const defaultTenant = account.tenants[0];
    const tenant = currentTenant ?? cachedTenant ?? defaultTenant;

    if (cachedTenant) {
        Logger.debug(
            'store',
            `found tenant ${cachedTenant.name} in list of tenants`,
            { object: account.tenants },
        );
    } else {
        Logger.warn(
            [ 'fallback', 'tenant' ],
            `current tenant fallback set to ${defaultTenant}, on cachedTenant: ${cachedTenant}`,
        );
    }

    if (currentTenant) {
        Logger.debug(
            'store',
            `on current tenant name ${currentTenant.name} selecting tenant`,
            { object: tenant },
        );
    } else {
        Logger.debug(
            'store',
            `no current tenant name in store, cached tenant ${cachedTenant?.name}, first tenant ${tenant.name}`,
            { object: tenant },
        );
    }

    localStorage.setItem(currentTenantStorageKey, tenant.name);

    if (
        currentTenantNameOrId &&
    !caseInsensitiveCompare(currentTenantNameOrId, tenant.name)
    ) {
        store.set('currentTenant', tenant.name);
    } else {
        store.set('currentTenantInfo', tenant);
    }
}

function getCachedTenant(account: Account) {
    const cachedTenantName = localStorage.getItem(currentTenantStorageKey);
    return cachedTenantName
        ? getTenantFromAccount(account, cachedTenantName)
        : null;
}

function getTenantFromAccount(
    account: Account | undefined,
    tenantNameOrId: string,
) {
    return (
        account?.tenants?.find(
            tenant =>
                caseInsensitiveCompare(tenant.name, tenantNameOrId) ||
        caseInsensitiveCompare(tenant.id, tenantNameOrId),
        ) ?? null
    );
}
